import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, input, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { EspaceService } from '@app/features/editeur/services/espace.service';
import { Espace, Periode, TypeEspace } from '@pcscol/odf-api-v1-angular';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { MatTooltip } from '@angular/material/tooltip';
import { NgStyle, AsyncPipe } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatOption } from '@angular/material/select';
import { EspaceChipComponent } from '../base-form/espace-chip/espace-chip.component';
import { MatCheckbox } from '@angular/material/checkbox';

/**
 * TODO : implémenter CVA
 */
@Component({
    selector: 'app-espace-selector',
    templateUrl: './espace-selector.component.html',
    styleUrls: ['./espace-selector.component.scss'],
    imports: [
        MatFormField,
        MatTooltip,
        NgStyle,
        MatLabel,
        MatInput,
        ReactiveFormsModule,
        MatAutocompleteTrigger,
        MatError,
        MatIcon,
        MatSuffix,
        MatAutocomplete,
        MatOption,
        EspaceChipComponent,
        MatCheckbox,
        AsyncPipe
    ]
})
export class EspaceSelectorComponent implements OnInit, OnDestroy, OnChanges {
    espaceService = inject(EspaceService);
    private fb = inject(FormBuilder);

    readonly style = input<object>();
    readonly propageSelection = input<boolean>(true);
    readonly label = input<string>('Espace');
    readonly tooltip = input<string>();
    readonly authorizedTypes = input<TypeEspace[]>([]);
    readonly required = input<boolean>(false);
    readonly disabled = input<boolean>(false);
    @Output() espaceSelected = new EventEmitter<Espace>();

    searchPeriodes$ = new BehaviorSubject<boolean>(false);

    /**
     * Empêche la récupération de l'espace actuellement sélectionné au niveau global
     */
    readonly useActualEspace = input<boolean>(true);
    readonly espaceActuelAutorise = input<boolean>(true);
    @Input() resetEspace?: Subject<void>;
    selectedEspace$ = this.espaceService.espaceSelected$;
    allTypes = TypeEspace;
    selectorLocked = false;

    private destroy$ = new Subject<void>();

    ngOnInit(): void {
        if (!this.espaceActuelAutorise()) {
            this.form.addValidators(ctrl => {
                if (ctrl.value && ctrl.value === this.espaceService.espaceSelected?.code) {
                    return { invalid: { message: `L'espace actuel ne peut pas être sélectionné` } };
                }
                return null;
            });
        }
        if (this.useActualEspace()) {
            this.selectedEspace$.pipe(takeUntil(this.destroy$)).subscribe((e: Espace | null) => {
                this.form.patchValue(e?.code || null);
                this.espaceSelected.emit(e || undefined);
                this.selectorLocked = e ? true : false;
            });
        }
        if (this.resetEspace) {
            this.resetEspace.pipe(takeUntil(this.destroy$)).subscribe(() => {
                this.form.patchValue(null);
            });
        }

        this.listeEspaces$ = this.searchPeriodes$.pipe(
            takeUntil(this.destroy$),
            switchMap((searchAllPeriodes: boolean) => {
                return combineLatest([
                    this.espaceService.espacesList$.pipe(
                        filter(l => l !== null),
                        map(list =>
                            list!
                                .filter(e => {
                                    const authorizedTypes = this.authorizedTypes();
                                    return !authorizedTypes.length || authorizedTypes.includes(e.typeEspace);
                                })
                                .sort((a, b) => a.code.localeCompare(b.code))
                        )
                    ),
                    this.form.valueChanges.pipe(distinctUntilChanged(), debounceTime(200), startWith(''))
                ]).pipe(
                    map(([liste, recherche]) => {
                        const uppercaseValue = recherche?.toUpperCase() ?? '';
                        return liste.filter(e => {
                            const periode = e as Periode;
                            return (
                                (searchAllPeriodes ||
                                    (periode.active === true &&
                                        periode.validite &&
                                        periode.validite.dateFin &&
                                        new Date(periode.validite.dateFin) >= new Date())) &&
                                (!recherche ||
                                    e.code.toUpperCase().includes(uppercaseValue) ||
                                    e.libelle.toUpperCase().includes(uppercaseValue))
                            );
                        });
                    })
                );
            })
        );

        if (this.disabled()) {
            this.form.disable();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.useActualEspace && changes.useActualEspace.currentValue === false) {
            this.form.patchValue(null);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    toggleAllPeriodes() {
        const currentValue = this.searchPeriodes$.value;
        this.searchPeriodes$.next(!currentValue);
    }

    form = this.fb.control<string | null>(null);

    listeEspaces$: Observable<Espace[]>;

    getDisplayValue = (espaces: Espace[]) => {
        return (value: string) => {
            return espaces.find(e => e.code === value)?.libelle || value;
        };
    };

    selectEspace(espace: MatAutocompleteSelectedEvent, liste: Espace[]) {
        const espaceSelected = liste.find(e => e.code === espace.option.value);
        this.espaceSelected.emit(espaceSelected);
        if (this.propageSelection()) {
            this.espaceService.selectEspace(espaceSelected || null);
        }
    }

    clear(): void {
        this.form.patchValue(null);
        if (this.propageSelection()) {
            this.espaceService.selectEspace(null);
        } else {
            this.espaceSelected.emit(undefined);
        }
    }
}
